import { useCallback, useEffect, useRef, useState } from "react";
import { Key } from "w3c-keys";

import { useAccessibleDropdown } from "@bokio/hooks/useAccessibleKeyboard/useAccessibleDropdown";
import { useClickOutside } from "@bokio/hooks/useClickOutside/useClickOutside";

import type { SelectOption } from "./SearchableSelectOption";
import type { CustomKeyboardBinding } from "@bokio/hooks/useAccessibleKeyboard/useAccessibleKeyboardbinding";
import type { ChangeEvent, MouseEventHandler, MutableRefObject } from "react";

interface UseSearchableSelectProps<T> {
	selected: SelectOption<T> | undefined;
	onChange: (selected: SelectOption<T> | undefined) => void;
	options: SelectOption<T>[];
	searchFn: (option: SelectOption<T>, term: string) => boolean;
	innerRef?: MutableRefObject<HTMLInputElement | null>;
	focusOnSelect?: boolean;
	autoSelectWhenClosingMenu: boolean;
	onScrollEnd?: () => void;
	initialSearchTerm?: string;
	restoreSelectedOnClear?: boolean;
}

export function useSearchableSelect<T>({
	selected,
	onChange,
	options,
	searchFn,
	innerRef,
	onScrollEnd,
	focusOnSelect = true,
	autoSelectWhenClosingMenu = true,
	initialSearchTerm = "",
	restoreSelectedOnClear = false,
}: UseSearchableSelectProps<T>) {
	const [selectedOption, setSelectedOption] = useState<SelectOption<T> | undefined>(selected);
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
	const displaySelected = !!selectedOption;

	const wrapperRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const scrollRef = useRef<HTMLDivElement>(null);

	const filteredOptions = (options || []).filter(option => searchFn(option, searchTerm));

	// Sync internal state when the selected prop changes from the parent
	useEffect(() => {
		// eslint-disable-next-line @eslint-react/set-state-in-effect
		setSelectedOption(selected);
	}, [selected]);

	const handleScroll = useCallback(() => {
		if (scrollRef.current) {
			const bottom = scrollRef.current.scrollTop + scrollRef.current.clientHeight + 50 > scrollRef.current.scrollHeight;
			bottom && onScrollEnd && onScrollEnd();
		}
	}, [onScrollEnd, scrollRef]);

	useEffect(() => {
		const scroll = scrollRef.current;
		scroll && scroll.addEventListener("scroll", handleScroll);

		return () => {
			scroll && scroll.removeEventListener("scroll", handleScroll);
		};
	}, [handleScroll, isOpen]);

	const focusInput = () => {
		inputRef.current && document.activeElement !== inputRef.current && inputRef.current.focus();
	};

	const toggleOpen = () => setIsOpen(open => !open);
	const close = () => {
		setIsOpen(false);
	};

	const clearSelection = () => {
		setSelectedOption(undefined);
		setSearchTerm("");
		onChange(undefined);
		focusInput();
	};

	const clearSelectionEvent: MouseEventHandler = e => {
		if (e) {
			// Stop click event from bubbling up to parent div which would cause `toggleOpen` to be invoked
			e.stopPropagation();
		}
		clearSelection();
	};

	const select = (option: SelectOption<T>) => {
		if (option.disabled) {
			return;
		}
		setSelectedOption(option);
		close();
		onChange(option);
		if (focusOnSelect) {
			focusInput();
		}
	};

	const customKeyBindings: CustomKeyboardBinding<SelectOption<T>>[] = [
		[Key.Tab, selectOrClose],
		[
			Key.Enter,
			(_, options, focusedIndex) => {
				if (!isOpen) {
					setIsOpen(true);
					return;
				}

				const focusedOption = options[focusedIndex];
				if (focusedOption) {
					select(focusedOption);
					focusInput();
				}
			},
		],
		[Key.ArrowUp, () => !isOpen && setIsOpen(true)],
		[Key.ArrowDown, () => !isOpen && setIsOpen(true)],
		[Key.Escape, () => close()],
		[Key.Delete, () => clearSelection()],
	];

	const { handleKeyDown, setOptionRef, resetFocusedIndex, focusedIndex } = useAccessibleDropdown<SelectOption<T>>({
		options: filteredOptions,
		customBindings: customKeyBindings,
	});

	function selectOrClose() {
		if (selectedOption || !autoSelectWhenClosingMenu) {
			close();
			return;
		}

		if (filteredOptions.length === 1) {
			select(filteredOptions[0]);
			return;
		}

		const focusedOption = filteredOptions[focusedIndex];
		if (focusedOption) {
			select(focusedOption);
			return;
		}

		if (searchTerm === "") {
			if (restoreSelectedOnClear && selected) {
				// Restore previous selection when user cleared search text and clicked away
				setSelectedOption(selected);
				onChange(selected);
			} else {
				setSelectedOption(undefined);
				onChange(undefined);
			}
			close();
			return;
		}

		const matches = filteredOptions.filter(o => searchFn(o, searchTerm));

		if (matches.length > 0) {
			select(matches[0]);
			return;
		}

		// No matches found â€” restore previous selection if one existed, otherwise clear
		if (restoreSelectedOnClear && selected) {
			setSelectedOption(selected);
			setSearchTerm("");
			onChange(selected);
		}

		close();
	}

	const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		resetFocusedIndex();
		setIsOpen(true);
		if (selectedOption) {
			setSelectedOption(undefined);
			onChange(undefined);
		}
	};

	useClickOutside({
		ref: wrapperRef,
		condition: isOpen,
		onClickOutside: selectOrClose,
	});

	const setInputRef = (ref: HTMLInputElement) => {
		if (!ref) {
			return;
		}
		inputRef.current = ref;
		if (innerRef) {
			innerRef.current = ref;
		}
	};

	return {
		actions: {
			handleSearchInputChange,
			handleKeyDown,
			toggleOpen,
			select,
			setOptionRef,
			setInputRef,
			focusInput,
			clearSelection: clearSelectionEvent,
		},
		refs: {
			dropdown: dropdownRef,
			wrapper: wrapperRef,
			input: inputRef,
			scroll: scrollRef,
		},
		searchTerm,
		selectedOption,
		displaySelected,
		filteredOptions,
		focusedIndex,
		isOpen,
	};
}
